import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Flex } from "src/components/Flex.tsx";
import { Button } from "src/components/Button/Button.tsx";
import { Heading } from "src/components/Heading/Heading.tsx";
import { TextV2 } from "src/components/TextV2/TextV2.tsx";
import { TranslationNamespaceEnum } from "src/lib/i18next/types.ts";
import { loadTranslations } from "src/lib/i18next/loadTranslations.ts";
import { retryAfterMs } from "src/lib/throttling.ts";
import * as styles from "./TooManyRequests.style.ts";

await loadTranslations(TranslationNamespaceEnum.TooManyRequests);

const SECOND = 1_000;

/**
 * Сервер попросил подождать: лимит запросов на пользователя исчерпан.
 *
 * Это не поломка, поэтому и экран другой: считаем вслух, сколько осталось до
 * сброса окна, и держим кнопку недоступной — повтор раньше срока получит ровно
 * тот же отказ и только продлит ожидание.
 */
export const TooManyRequests = ({
  error,
  onReset,
}: {
  error: unknown;
  onReset?: () => void;
}) => {
  const { t } = useTranslation(TranslationNamespaceEnum.TooManyRequests);
  const [secondsLeft, setSecondsLeft] = useState(() =>
    Math.ceil(retryAfterMs(error) / SECOND),
  );

  useEffect(() => {
    if (secondsLeft <= 0) {
      return;
    }

    const timer = setTimeout(
      () => setSecondsLeft((value) => value - 1),
      SECOND,
    );

    return () => clearTimeout(timer);
  }, [secondsLeft]);

  return (
    <div css={styles.root}>
      <div css={styles.inner}>
        <div css={styles.errorCode}>429</div>
        <Heading css={styles.heading} h={1}>
          {t("title")}
        </Heading>

        <Flex flexShrink={0} css={styles.description}>
          <TextV2 variant={"body1"}>{t("description")}</TextV2>
        </Flex>

        <Flex flexShrink={0} style={{ marginTop: "20px", minHeight: "0px" }}>
          <Button
            onClick={onReset}
            disabled={secondsLeft > 0}
            variant={"filled"}
            startAdornment={<i className={"icon-15-update"} />}
          >
            {secondsLeft > 0
              ? t("waiting", { seconds: secondsLeft })
              : t("action")}
          </Button>
        </Flex>
      </div>
    </div>
  );
};
